import React, { useEffect, useMemo, useState } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel,
} from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/16/solid';
import { Command, CommandGroup, rankCommands } from './commandRanking';

/**
 * Linear's ⌘K palette (#603 follow-on 4): one box that jumps anywhere and
 * runs the safe actions. What it searches is the caller's business — each
 * app's provider builds the groups and gates them by role; this only
 * draws, ranks and runs. Identical in both apps: front-porch
 * `src/shell/`, member-portal `src/Components/Shell/`.
 *
 * Nothing that moves money belongs in here. A palette makes an action one
 * keystroke from anywhere, which is the point for "new violation" and
 * exactly wrong for "post payment".
 */
interface Props {
  open: boolean;
  onClose: () => void;
  groups: CommandGroup[];
  /** Still fetching something searchable; said out loud rather than showing "no results". */
  loading?: boolean;
  placeholder?: string;
}

const isMac = () =>
  typeof navigator !== 'undefined' &&
  /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

/** How to write the shortcut on this machine: "⌘K" or "Ctrl K". */
export const paletteShortcutLabel = () => (isMac() ? '⌘K' : 'Ctrl K');

/**
 * ⌘K / Ctrl+K from anywhere — including inside a text field, as in
 * Linear — except over some other dialog, where it would stack a second
 * modal on a half-filled form. Pressed while the palette is open, it
 * closes it.
 */
export function usePaletteShortcut(
  enabled: boolean,
  isOpen: boolean,
  setOpen: (open: boolean) => void
) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (
        !(e.metaKey || e.ctrlKey) ||
        e.altKey ||
        e.shiftKey ||
        e.key.toLowerCase() !== 'k'
      )
        return;
      if (!isOpen && document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      setOpen(!isOpen);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled, isOpen, setOpen]);
}

export function CommandPalette({
  open,
  onClose,
  groups,
  loading = false,
  placeholder = 'Type a command or search…',
}: Props) {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  const results = useMemo(() => rankCommands(groups, query), [groups, query]);
  const first = results[0]?.items[0] ?? null;

  // Run once the dialog has finished closing (its leave transition is
  // 75ms), not inside the Combobox's change handler: an action that opens
  // a dialog of its own — new violation — would otherwise have its focus
  // taken back by this one's focus trap on the way out.
  const choose = (c: Command | null) => {
    if (!c) return;
    onClose();
    window.setTimeout(c.run, 100);
  };

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75"
      />
      <div className="fixed inset-0 overflow-y-auto px-4 pt-[12vh] sm:px-6">
        <DialogPanel
          transition
          className="mx-auto max-w-xl overflow-hidden rounded-xl border border-line-strong bg-raised shadow-2xl transition data-[closed]:scale-[0.98] data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75"
        >
          <Combobox<Command | null> value={null} onChange={choose}>
            <div className="flex items-center gap-2.5 border-b border-line px-4">
              <MagnifyingGlassIcon
                className="h-4 w-4 shrink-0 text-fg-muted"
                aria-hidden
              />
              <ComboboxInput
                autoFocus
                aria-label="Search or run a command"
                placeholder={placeholder}
                displayValue={() => query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  // Enter with nothing highlighted takes the top result, so
                  // "type, Enter" works without reaching for the arrows.
                  if (
                    e.key === 'Enter' &&
                    first &&
                    !document.querySelector('[data-palette-option][data-focus]')
                  ) {
                    e.preventDefault();
                    choose(first);
                  }
                }}
                className="h-12 w-full border-0 bg-transparent p-0 text-[15px] text-fg placeholder:text-fg-muted focus:outline-none focus:ring-0"
              />
            </div>

            <ComboboxOptions
              static
              className="max-h-[min(60vh,26rem)] scroll-py-2 overflow-y-auto py-2 empty:hidden"
            >
              {results.map((g) => (
                <div
                  key={g.key}
                  role="group"
                  aria-labelledby={`palette-group-${g.key}`}
                >
                  <div
                    id={`palette-group-${g.key}`}
                    className="px-4 pb-1 pt-2 text-[11px] font-medium text-fg-muted"
                  >
                    {g.label}
                  </div>
                  {g.items.map((c) => (
                    <ComboboxOption
                      key={c.id}
                      value={c}
                      data-palette-option=""
                      className="group mx-2 flex cursor-default select-none items-center gap-3 rounded-md px-2 py-1.5 text-[13px] text-fg-secondary data-[focus]:bg-fg/[0.07] data-[focus]:text-fg"
                    >
                      <span
                        className="flex h-4 w-4 shrink-0 items-center justify-center text-fg-muted"
                        aria-hidden
                      >
                        {c.icon}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {c.label}
                        {c.detail && (
                          <>
                            <span className="sr-only">, </span>
                            <span className="ml-2 text-fg-muted">{c.detail}</span>
                          </>
                        )}
                      </span>
                      {c.hint && (
                        <span
                          className="shrink-0 text-[11px] text-fg-muted opacity-0 group-data-[focus]:opacity-100"
                          aria-hidden
                        >
                          {c.hint}
                        </span>
                      )}
                    </ComboboxOption>
                  ))}
                </div>
              ))}
            </ComboboxOptions>

            {query.trim() && results.length === 0 && (
              <p className="px-4 py-8 text-center text-[13px] text-fg-muted">
                {loading
                  ? 'Still loading — one moment…'
                  : `Nothing matches “${query.trim()}”.`}
              </p>
            )}
            {loading && results.length > 0 && (
              <p
                className="border-t border-line px-4 py-1.5 text-[11px] text-fg-muted"
                aria-live="polite"
              >
                Loading more results…
              </p>
            )}
          </Combobox>

          <div className="flex items-center gap-4 border-t border-line px-4 py-2 text-[11px] text-fg-muted max-sm:hidden">
            <span>
              <Kbd>↑</Kbd> <Kbd>↓</Kbd> to move
            </span>
            <span>
              <Kbd>↵</Kbd> to open
            </span>
            <span>
              <Kbd>esc</Kbd> to close
            </span>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}

const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="rounded border border-line px-1 font-sans text-[10px] text-fg-secondary">
    {children}
  </kbd>
);
